import React, { Suspense, useState, useEffect, useMemo, useCallback } from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import ReduxToastr, { toastr } from 'react-redux-toastr';
import { I18n } from 'src/translations/i18n';
import { makeStyles, Portal } from '@material-ui/core';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import { find, isEmpty, pathOr, propEq } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import { ErrorBoundary } from '@sentry/react';
import useSuperRights from 'src/hooks/settings/useSuperRights';
import { setPersonalConfiguration } from 'src/store/actions/personalConfigActions';
import { loadUserInfo, loadVersions, loadUserData } from 'src/store/actions/userActions';
import { loadConfiguration, loadConfigurationsByKeywords } from 'src/store/actions/configurationActions';
import { ConfigurationsContext } from 'src/contextProviders/ConfigContext';
import { UserRightsContext, superFlags, defaultFlags } from 'src/contextProviders/UserRightsContext';
import { getPersonalConfiguration, getGlobalPeriod } from 'src/store/reducers/personalConfigurationReducer';
import Loading from 'src/components/Loading';
import GeneralLayout from 'src/components/app/GeneralLayout';
import RouteErrorFallback from 'src/components/RouteErrorFallback';
import { selectUserInfo, selectUserInfoFetching, selectUserInfoLoaded, selectUserRights, selectIsSuper, selectIsManagerMode } from 'src/store/selectors/userInfo';
import { selectConfigurations, selectModules } from 'src/store/selectors/configurations';
import useThemeSwitch from 'src/style/themes/useThemeSwitch';
import getThemedClasses from 'src/style/themes/themedClasses';
import useOneSessionClients from 'src/hooks/boot/useOneSessionClients';
import { loadEmployeesPromise } from 'src/store/actions/employeesActions';
import { DO_NOTHING } from 'src/helpers/FPUtils';
import usePageTitle from 'src/hooks/boot/usePageTitle';
import useChatWidget from 'src/hooks/boot/useChatWidget';
import { useAuth } from 'react-oidc-context';
import CompaniesDialog from 'src/components/app/CompaniesDialog';
import ConfigLoader from 'src/components/app/ConfigLoader';
import UpdateSnackBar from 'src/components/app/UpdateSnackBar';
import { themes } from 'src/style/themes';
import userManager from 'src/auth/userManager';
import useRedirectAfterLogin from 'src/hooks/useRedirectAfterLogin';
import useEntityInspectorListener from 'src/components/EntityInspector/useEntityInspectorListener';
import { setInitialAppLang } from 'src/store/actions/translationActions';
import SelfServiceMenu from '../SelfService';
import SettingsMenu from '../Settings';
import ReportMenu from '../Reports';
import HomePage from '../Home';
import LoginForm from '../Login';
import Callback from '../Login/Callback';
const Absences = React.lazy(() => import('../Absences'));
const Calendar = React.lazy(() => import('../Calendar/Calendar'));
const CalendarEventsLogContainer = React.lazy(() => import('../Calendar/CalendarEventsLog'));
const CalendarFaultyEventsContainer = React.lazy(() => import('../Calendar/FaultyEvents'));
const DoorMonitoring = React.lazy(() => import('../DoorMonitoring'));
const EmployeesContainer = React.lazy(() => import('../Employees/General'));
const EmployeesContracts = React.lazy(() => import('../Employees/Contracts'));
const EmployeesRequests = React.lazy(() => import('../Employees/EmployeesRequests'));
const EmployeesStatements = React.lazy(() => import('../Employees/EmployeesStatements'));
const EmployeesTable = React.lazy(() => import('../Employees/Table'));
const Help = React.lazy(() => import('../Help'));
const OfflineLockCards = React.lazy(() => import('../OfflineLockCards'));
const PersonCalendar = React.lazy(() => import('../Calendar/PersonCalendar'));
const PlannerContainer = React.lazy(() => import('../Planner'));
const AdvancedPlannerContainer = React.lazy(() => import('../AdvancedPlanner'));
const ProjectsContainer = React.lazy(() => import('../Projects'));
const RewardRegistry = React.lazy(() => import('../RewardRegistry'));
const TimeTable = React.lazy(() => import('../TimeTable'));
const TvJobs = React.lazy(() => import('../TvJobs'));
const PassagesMenu = React.lazy(() => import('../Passages'));
const useStyles = makeStyles(() => ({
    root: {
        flexGrow: 1,
        height: '100%',
        zIndex: 1,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex'
    }
}));
const getModule = (modules, moduleName) => {
    switch (moduleName) {
        case 'ImportFromArgos': return find(propEq('Type', 'Argos'), modules || []);
        case 'EmployeesRequests': return find(propEq('Type', 'Planner'), modules || []);
        default: return find(propEq('Type', moduleName), modules || []);
    }
};
function App() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [theme] = useThemeSwitch();
    const auth = useAuth();
    const location = useLocation();
    const { saveCurrentLocation } = useRedirectAfterLogin();
    useSuperRights(true);
    useOneSessionClients();
    usePageTitle();
    useEntityInspectorListener();
    useChatWidget();
    const userRights = useSelector(selectUserRights);
    const userInfo = useSelector(selectUserInfo);
    const userInfoLoading = useSelector(selectUserInfoFetching);
    const userInfoLoaded = useSelector(selectUserInfoLoaded);
    const configurations = useSelector(selectConfigurations);
    const modules = useSelector(selectModules);
    const isSuper = useSelector(selectIsSuper);
    const isManagerMode = useSelector(selectIsManagerMode);
    const [companiesOptions, setCompaniesOptions] = useState([]);
    const [selectorDialogOpened, setSelectorDialogOpened] = useState(false);
    const themeConfig = useMemo(() => {
        // @ts-ignore
        const themeObj = createTheme(themes[theme]);
        const themedClasses = getThemedClasses(themeObj);
        return { ...themeObj, themedClasses };
    }, [theme]);
    const isModuleEnabled = useCallback((moduleName) => {
        const module = getModule(modules, moduleName);
        return module?.Enabled ?? false;
    }, [modules]);
    const checkUserRights = useCallback((id, prop, checkVisibility = false) => {
        const { Visible = [], ReadWrite = [] } = userRights;
        const collection = checkVisibility ? Visible : ReadWrite;
        const object = find(propEq('Id', id), collection);
        const flags = isSuper ? superFlags : pathOr(defaultFlags, ['Flags'], object);
        // @ts-ignore
        return prop ? flags[prop] : flags;
    }, [isSuper, userRights]);
    const loadAppData = useCallback(async (userId) => {
        const critical = [
            dispatch(loadUserData({ id: userId }))
                .then((userData) => {
                return dispatch(setInitialAppLang(userData.Language));
            }).catch(DO_NOTHING),
            dispatch(loadVersions()).catch(DO_NOTHING),
            dispatch(loadConfiguration()).catch(DO_NOTHING),
            dispatch(loadConfigurationsByKeywords()).catch(DO_NOTHING)
        ];
        const important = [
            dispatch(loadEmployeesPromise()).catch(DO_NOTHING)
        ];
        await Promise.all(critical);
        await Promise.all(important);
    }, [dispatch]);
    const user = auth.user?.profile?.name;
    useEffect(() => {
        if (user && (!auth.isAuthenticated || auth.user?.expired)) {
            userManager.signinSilent().catch(() => {
                saveCurrentLocation();
                userManager.signoutRedirect();
            });
        }
        else if (user
            && isEmpty(userInfo)
            && !userInfoLoading
            && !userInfoLoaded
            && location.pathname !== '/tvJobs') {
            dispatch(loadUserInfo())
                .then((usrInfo) => {
                loadAppData(usrInfo.UserId);
            })
                .catch((reject) => {
                toastr.warning(I18n.t('MultipleChoicesAvailable'), '');
                setCompaniesOptions(reject.data.Data);
                setSelectorDialogOpened(true);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, user, userInfo, userInfoLoading, userInfoLoaded, location, loadAppData, dispatch]);
    useEffect(() => {
        const personalConfig = getPersonalConfiguration();
        const globalPeriod = getGlobalPeriod();
        dispatch(setPersonalConfiguration({ ...personalConfig, globalPeriod }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(MuiThemeProvider, { theme: themeConfig },
        React.createElement(ConfigurationsContext.Provider, { value: { configurations, isModuleEnabled } },
            React.createElement(UserRightsContext.Provider, { value: { isSuper, userRights, checkUserRights } },
                React.createElement(DndProvider, { backend: Backend },
                    React.createElement("div", { className: classes.root },
                        React.createElement(UpdateSnackBar, null),
                        React.createElement(CompaniesDialog, { options: companiesOptions, isOpen: selectorDialogOpened }),
                        React.createElement(Portal, { container: document.body },
                            React.createElement(ReduxToastr, { transitionIn: "fadeIn", timeOut: 10000, transitionOut: "fadeOut", position: "top-right" })),
                        React.createElement(Suspense, { fallback: React.createElement(ConfigLoader, null) },
                            React.createElement(Switch, null,
                                React.createElement(Route, { path: "/login", component: LoginForm }),
                                React.createElement(Route, { path: "/callback", component: Callback }),
                                React.createElement(Route, { path: "/tvJobs", component: TvJobs }),
                                React.createElement(Route, { render: () => (React.createElement(GeneralLayout, null,
                                        React.createElement(Suspense, { fallback: React.createElement(Loading, null) },
                                            React.createElement(ErrorBoundary, { fallback: RouteErrorFallback }, isManagerMode
                                                ? (React.createElement(Switch, null,
                                                    React.createElement(Route, { exact: true, path: "/", component: HomePage }),
                                                    React.createElement(Route, { path: "/reports", component: ReportMenu }),
                                                    React.createElement(Route, { path: "/passages", component: PassagesMenu }),
                                                    React.createElement(Route, { path: "/settings", component: SettingsMenu }),
                                                    React.createElement(Route, { path: "/help", component: Help }),
                                                    React.createElement(Route, { path: "/planner", component: PlannerContainer }),
                                                    React.createElement(Route, { path: "/advancedPlanner", component: AdvancedPlannerContainer }),
                                                    React.createElement(Route, { path: "/absences", component: Absences }),
                                                    React.createElement(Route, { path: "/projects", component: ProjectsContainer }),
                                                    React.createElement(Route, { path: "/employees/table", component: EmployeesTable }),
                                                    React.createElement(Route, { path: "/employees/contracts", component: EmployeesContracts }),
                                                    React.createElement(Route, { path: "/employees/employeesRequests", component: EmployeesRequests }),
                                                    React.createElement(Route, { path: "/employees/statements", component: EmployeesStatements }),
                                                    React.createElement(Route, { path: "/employees", component: EmployeesContainer }),
                                                    React.createElement(Route, { path: "/calendar/personCalendar", component: PersonCalendar }),
                                                    React.createElement(Route, { path: "/calendar/faultyEvents", component: CalendarFaultyEventsContainer }),
                                                    React.createElement(Route, { path: "/calendar/calendarEventsLog", component: CalendarEventsLogContainer }),
                                                    React.createElement(Route, { path: "/timeTable", component: TimeTable }),
                                                    React.createElement(Route, { path: "/calendar", component: Calendar }),
                                                    React.createElement(Route, { path: "/selfService", component: SelfServiceMenu }),
                                                    React.createElement(Route, { path: "/doorMonitoring", component: DoorMonitoring }),
                                                    React.createElement(Route, { path: "/offlineLockCards", component: OfflineLockCards }),
                                                    React.createElement(Route, { path: "/rewardRegistry", component: RewardRegistry }),
                                                    React.createElement(Route, null,
                                                        React.createElement(Redirect, { to: "/" }))))
                                                : (React.createElement(Switch, null,
                                                    React.createElement(Route, { path: "/selfService", component: SelfServiceMenu }),
                                                    React.createElement(Route, { path: "/help", component: Help }),
                                                    React.createElement(Route, null,
                                                        React.createElement(Redirect, { to: "/selfService" })))))))) })))))))));
}
export default App;
